<template>
    <textarea
        :ref="disableAutosize ? undefined : 'textarea'"
        v-model="input"
        :aria-label="props['aria-label']"
        class="base-text-area fs-mask"
        :class="[!disableAutosize && 'textareaScroll field-sizing-content', showResizeHandle && 'base-text-area--show-resizer']" />
</template>

<script setup lang="ts">
/**
 * BaseTextarea exposes a named model "input" which can be bound using `v-model:input` from the parent component
 * to establish 2 way data binding with the value specified in this textarea
 */
import { useTextareaAutosize } from "@vueuse/core";
import { type TextareaHTMLAttributes, useAttrs, watch } from "vue";

interface BaseTextareaProps extends /* @vue-ignore */ TextareaHTMLAttributes {
    disableAutosize?: boolean;
    showResizeHandle?: boolean;
}

const { disableAutosize = false, showResizeHandle = false, ...props } = defineProps<BaseTextareaProps>();

const attr = useAttrs();
// NOTE: as of 6/8/26 there is a field-sizing: content CSS that is not yet supported in Firefox,
// when this is widely available we will no longer need this composable from vueuse and can achieve autosizing via CSS
const { textarea, input } = useTextareaAutosize({ styleProp: attr.rows ? "minHeight" : undefined });

// a model to two way bind with the parent consumer of this component, below watchers sync this model with the model on the textarea
const model = defineModel<string>("input", { default: "" });
watch(
    model,
    (newValue) => {
        if (input.value !== newValue) {
            input.value = newValue;
        }
    },
    { immediate: true },
);
watch(input, (newValue) => {
    if (model.value !== newValue) {
        model.value = newValue;
    }
});

defineExpose({ textareaRef: textarea });
</script>

<style scoped>
@reference "../../../../css/tailwind.css";
.textareaScroll {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.textareaScroll::-webkit-scrollbar {
    display: none;
}

textarea:not(.base-text-area--show-resizer)::-webkit-resizer {
    display: none;
}

@layer components {
    .base-text-area {
        @apply w-full body-small focus-visible:outline-hidden disabled:bg-transparent disabled:text-control-text wrap-anywhere;
    }
}
</style>
